/**
 * Production HTTP transport that ships a telemetry batch to the
 * `Telemetry.Endpoints` Container App via APIM. The client (in `client.ts`)
 * depends only on the {@link TelemetryTransport} interface; this file is
 * the default implementation.
 *
 * Two delivery paths:
 *   - {@link HttpTelemetryTransport.send} - `fetch` POST with `keepalive`,
 *     bearer token, gzipped (when the browser supports `CompressionStream`),
 *     correlation header. Returns true on 2xx.
 *   - {@link HttpTelemetryTransport.sendBeacon} - fire-and-forget
 *     `navigator.sendBeacon` for the `pagehide` flush path. No bearer
 *     header (sendBeacon can't set custom headers); auth is via a short-
 *     lived signed ticket the caller refreshes periodically.
 *
 * Both paths swallow exceptions and return `false` on failure - the client
 * relies on the boolean signal to decide whether to clear the outbox.
 */

import type { TelemetryEvent } from "./types";
import type { TelemetryTransport } from "./client";

/** Token retrieval callback. Resolves to a fresh bearer or `null` on failure. */
export type BearerTokenProvider = () => Promise<string | null>;

/** Short-lived beacon ticket provider (for the sendBeacon path). */
export type BeaconTicketProvider = () => Promise<string | null>;

export interface HttpTelemetryTransportOptions {
  /** Absolute URL of the POST endpoint. Required. */
  endpoint: string;
  /** Resolves a bearer token for the normal POST. */
  getBearerToken: BearerTokenProvider;
  /**
   * Silently re-acquires a fresh bearer after a 401. Called once per failed
   * send; when it yields a token the POST is retried with it. Mirrors the
   * reactive 401 retry in `shared/api/fetchWithRefresh`. Optional - when absent
   * (tests / pre-auth) a 401 just fails the batch as before.
   */
  refreshBearerToken?: BearerTokenProvider;
  /** Resolves a beacon ticket for the unload path. Optional - null disables sendBeacon auth. */
  getBeaconTicket?: BeaconTicketProvider;
  /** Override `fetch`. Defaults to `globalThis.fetch`. Test seam. */
  fetchImpl?: typeof fetch;
  /** Override `navigator.sendBeacon`. Test seam. */
  beaconImpl?: (url: string, body: BodyInit | null) => boolean;
}

export class HttpTelemetryTransport implements TelemetryTransport {
  constructor(private readonly options: HttpTelemetryTransportOptions) {
    if (!options.endpoint) throw new Error("HttpTelemetryTransport: endpoint is required");
    if (!options.getBearerToken)
      throw new Error("HttpTelemetryTransport: getBearerToken is required");
  }

  async send(events: readonly TelemetryEvent[]): Promise<boolean> {
    if (events.length === 0) return true;
    const token = await this.resolveToken();
    if (!token) return false;

    let res = await this.post(events, token);
    if (res === null) return false; // network throw - keep the batch for retry

    res = await this.retryOn401(events, res);
    if (res === null) return false;

    return res.ok;
  }

  /** Fetch the bearer, swallowing a throw (treated as "no token → don't send"). */
  private async resolveToken(): Promise<string | null> {
    try {
      return await this.options.getBearerToken();
    } catch {
      return null;
    }
  }

  /**
   * On a 401, silently re-acquire the bearer once and retry (the OIDC idToken
   * lives ~60 min and isn't always refreshed in time outside an active session).
   * Returns the retried `Response`, or the original `res` when there's no
   * refresher / it yields no token, or `null` on a network throw during retry.
   * Mirrors the reactive retry in `shared/api/fetchWithRefresh`.
   */
  private async retryOn401(
    events: readonly TelemetryEvent[],
    res: Response,
  ): Promise<Response | null> {
    if (res.status !== 401 || !this.options.refreshBearerToken) return res;
    let fresh: string | null;
    try {
      fresh = await this.options.refreshBearerToken();
    } catch {
      fresh = null;
    }
    return fresh ? this.post(events, fresh) : res;
  }

  /**
   * POST a batch with the given bearer. Returns the `Response`, or `null` when
   * the fetch throws (network error) so the caller keeps the batch queued.
   */
  private async post(
    events: readonly TelemetryEvent[],
    token: string,
  ): Promise<Response | null> {
    const fetchImpl = this.options.fetchImpl ?? globalThis.fetch;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    // Correlation id rides as an `X-Correlation-Id` request header so the
    // server's CorrelationIdMiddleware ties every log line emitted while
    // ingesting this POST back to the same client trace. A batch shares a
    // single request-level id - we use the first event's correlation id (the
    // per-event id also stays in the body for row-level correlation).
    const correlationId = firstCorrelationId(events);
    if (correlationId) {
      headers["X-Correlation-Id"] = correlationId;
    }

    // Send plain JSON. We do NOT gzip: the ingest endpoint (through APIM) does
    // not inflate a `Content-Encoding: gzip` body — it parses the compressed
    // bytes as JSON and 400s ("'0x1F' is an invalid start of a value"). Batches
    // are tiny (≤20 events, well under the 64 KB keepalive cap), so compression
    // bought little anyway.
    const body: BodyInit = JSON.stringify({ events });

    try {
      return await fetchImpl(this.options.endpoint, {
        method: "POST",
        headers,
        body,
        // keepalive lets the request finish even if the user navigates away
        // mid-send. Browsers cap keepalive bodies at 64 KB - our batches at
        // 20 events x ~1 KB each (and smaller once gzipped) are well under that.
        keepalive: true,
      });
    } catch {
      return null;
    }
  }

  sendBeacon(events: readonly TelemetryEvent[]): boolean {
    if (events.length === 0) return true;

    // navigator.sendBeacon can't set custom headers, so we use a signed
    // ticket on the URL instead of a bearer header. If the ticket provider
    // isn't configured (e.g. dev), we fail the beacon - events stay in the
    // outbox for the next online flush.
    const ticketProvider = this.options.getBeaconTicket;
    if (!ticketProvider) return false;

    // The ticket lookup must be synchronous from the beacon's perspective -
    // by `pagehide` we don't have time to await anything. Production wiring
    // refreshes the ticket on a timer and caches the latest value;
    // we read that cached value via a sync wrapper passed in by the host.
    // Here we just call the provider; if it returns a Promise we treat the
    // beacon as unconfigured.
    const ticket = ticketProvider() as unknown;
    if (typeof ticket !== "string" || !ticket) return false;

    const url = `${this.options.endpoint}?ticket=${encodeURIComponent(ticket)}`;
    const beaconImpl =
      this.options.beaconImpl ??
      (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
        ? navigator.sendBeacon.bind(navigator)
        : undefined);
    if (!beaconImpl) return false;

    try {
      return beaconImpl(
        url,
        new Blob([JSON.stringify({ events })], { type: "application/json" }),
      );
    } catch {
      return false;
    }
  }
}

/** First non-empty correlation id across the batch, or `undefined`. */
function firstCorrelationId(events: readonly TelemetryEvent[]): string | undefined {
  for (const event of events) {
    if (event.correlationId) return event.correlationId;
  }
  return undefined;
}
